import { useEffect, useRef, useState } from "react";
import { Autocomplete, TextField, CircularProgress, IconButton, Tooltip } from "@mui/material";
import AccountTreeIcon from "@mui/icons-material/AccountTree";
import { useDispatch, useSelector } from "react-redux";
import { fromEvent } from "rxjs";
import { debounceTime, switchMap } from "rxjs/operators";
import { AppDispatch } from "../../reduxStore/Store";
import { AutocompleteProps } from "@mui/material/Autocomplete";
import { FamilleData } from "../../models/models";
import { GeneriqueDataType } from "../../constants/enums";
import {
  fetchFamillesAutocomplete,
  clearFamilleAutocomplete,
  FamilleAutocompleteSlot,
} from "../../reduxStore/FamilleSlice";
import FamilleTreeDialog from "./FamilleTreeDialog";

interface FamilleAutoCProps
  extends Partial<AutocompleteProps<FamilleData, false, false, false>> {
  slot: FamilleAutocompleteSlot;
  type: GeneriqueDataType;
  onFamilleChange: (famille: FamilleData | null) => void;
  selectedFamille?: FamilleData | null;
  excludeId?: number;
  label?: string;
  /** Bouton d'ouverture de l'arborescence complète. Défaut : true. */
  showTree?: boolean;
}

const FamilleAutoC: React.FC<FamilleAutoCProps> = ({
  slot,
  type,
  onFamilleChange,
  selectedFamille,
  excludeId,
  label = "Famille",
  showTree = true,
  ...autocompleteProps
}) => {
  const [treeOpen, setTreeOpen] = useState(false);
  const dispatch = useDispatch<AppDispatch>();
  const { autocompleteMain, autocompleteParent, autocompleteLoading } = useSelector(
    (state: any) => state.FamilleSlice
  );
  const options: FamilleData[] = slot === 'main' ? autocompleteMain : autocompleteParent;

  const [value, setValue] = useState<FamilleData | null>(null);
  const inputRef = useRef<HTMLInputElement | null>(null);

  useEffect(() => {
    setValue(selectedFamille ?? null);
  }, [selectedFamille]);

  useEffect(() => {
    if (!inputRef.current) return;
    const subscription = fromEvent(inputRef.current, "input")
      .pipe(
        debounceTime(400),
        switchMap((event: any) => {
          const nom = event.target.value.trim();
          if (nom.length > 1) {
            dispatch(fetchFamillesAutocomplete({ nom, excludeId, slot, type }));
          }
          return [];
        })
      )
      .subscribe();
    return () => subscription.unsubscribe();
  }, [dispatch, excludeId, slot, type]);

  const handleChange = (_: any, newValue: FamilleData | null) => {
    setValue(newValue);
    onFamilleChange(newValue);
  };

  return (
    <>
    <Autocomplete
      options={options}
      value={value}
      getOptionLabel={(option: FamilleData) => option.nom}
      isOptionEqualToValue={(option, val) => option.id === val.id}
      loading={autocompleteLoading}
      onChange={handleChange}
      onBlur={() => dispatch(clearFamilleAutocomplete(slot))}
      {...autocompleteProps}
      renderInput={(params) => (
        <TextField
          {...params}
          inputRef={inputRef}
          label={label}
          variant="outlined"
          size="small"
          // place pour l'icône arborescence en plus des icônes effacer/ouvrir de MUI
          sx={showTree && !autocompleteProps.disabled ? { "& .MuiOutlinedInput-root": { pr: "93px !important" } } : undefined}
          InputProps={{
            ...params.InputProps,
            endAdornment: (
              <>
                {autocompleteLoading ? <CircularProgress color="inherit" size={20} /> : null}
                {showTree && !autocompleteProps.disabled && (
                  <Tooltip title="Parcourir l'arborescence">
                    <IconButton
                      size="small"
                      onClick={(e) => { e.stopPropagation(); setTreeOpen(true); }}
                      onMouseDown={(e) => e.preventDefault()}
                      sx={{ p: 0.4, color: "#667085", "&:hover": { color: "#2563eb" } }}
                    >
                      <AccountTreeIcon sx={{ fontSize: 18 }} />
                    </IconButton>
                  </Tooltip>
                )}
                {params.InputProps.endAdornment}
              </>
            ),
          }}
        />
      )}
    />
    {showTree && (
      <FamilleTreeDialog
        open={treeOpen}
        onClose={() => setTreeOpen(false)}
        type={type}
        title={label}
        selectedId={value?.id ?? null}
        excludeId={excludeId}
        onSelect={(f) => handleChange(null, f)}
      />
    )}
    </>
  );
};

export default FamilleAutoC;
